import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import AppHeader from '../components/AppHeader';
import Icon from '../components/Icon';
import { EmptySignal } from '../components/illustrations';

export default function CheckoutResult({ outcome }) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { reloadLicense } = useAuth();
  const oid = params.get('oid');

  const [state, setState] = useState(outcome === 'failed' ? 'failed' : 'pending');
  const tries = useRef(0);

  useEffect(() => {
    if (outcome === 'failed' || !oid) return undefined;
    let stop = false;
    const poll = async () => {
      tries.current += 1;
      try {
        const res = await api.confirmPayment(oid);
        if (stop) return;
        if (res.status === 'success' || res.subscriptionStatus === 'active') {
          setState('success');
          await reloadLicense();
          return;
        }
        if (res.status === 'failed') { setState('failed'); return; }
      } catch (_) { /* devam et */ }
      if (tries.current < 20 && !stop) setTimeout(poll, 2000);
      else if (!stop) setState('slow');
    };
    poll();
    return () => { stop = true; };
  }, [oid, outcome, reloadLicense]);

  const content = {
    pending: { icon: 'activity', title: t('checkout.pending_title'), desc: t('checkout.pending_desc') },
    slow: { icon: 'activity', title: t('checkout.pending_title'), desc: t('checkout.pending_desc') },
    success: { icon: 'check', title: t('checkout.success_title'), desc: t('checkout.success_desc') },
    failed: { icon: 'alert-triangle', title: t('checkout.failed_title'), desc: t('checkout.failed_desc') },
  }[state];

  return (
    <>
      <AppHeader />
      <div className="container page-enter" style={{ maxWidth: 480, padding: '80px 20px', textAlign: 'center' }}>
        <div style={{ display: 'inline-grid', placeItems: 'center', width: 56, height: 56, borderRadius: '50%',
          background: state === 'success' ? 'var(--ok-soft)' : state === 'failed' ? 'var(--crit-soft)' : 'var(--accent-soft)',
          color: state === 'success' ? 'var(--ok)' : state === 'failed' ? 'var(--crit)' : 'var(--accent)', marginBottom: 20 }}>
          <Icon name={content.icon} size={26} />
        </div>
        <h1 style={{ fontSize: '1.6rem', marginBottom: 8 }}>{content.title}</h1>
        <p style={{ color: 'var(--text-3)', marginBottom: 28 }}>{content.desc}</p>

        {state === 'success' && (
          <button className="btn" onClick={() => navigate('/onboarding')}>
            {t('checkout.success_cta')} <Icon name="arrow" size={14} />
          </button>
        )}
        {(state === 'failed' || state === 'slow') && (
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <Link to="/checkout" className="btn btn-subtle btn-sm">{t('checkout.retry')}</Link>
            {state === 'slow' && <Link to="/dashboard" className="btn btn-sm">{t('nav.dashboard')}</Link>}
          </div>
        )}
        {(state === 'pending') && <div style={{ marginTop: 10 }}><EmptySignal size={90} /></div>}
      </div>
    </>
  );
}
